import { useEffect, useRef } from 'react';
import type { StudioState } from '../store/types';
import { projectApi, diagramApi } from '../lib/api-client';

type SetStore = React.Dispatch<React.SetStateAction<StudioState>>;

/**
 * Syncs local-only projects & diagrams (nanoid IDs) to the backend,
 * replacing local IDs with server-generated UUIDs.
 *
 * Runs once when workspaceId first becomes available after auth.
 */
export function useServerSync(
  workspaceId: string | undefined,
  store: StudioState,
  setStore: SetStore,
) {
  const syncedRef = useRef(false);
  // Snapshot the store at sync time so we don't re-run on every store change
  const storeRef = useRef(store);
  storeRef.current = store;

  useEffect(() => {
    if (!workspaceId || syncedRef.current) return;
    syncedRef.current = true;

    const run = async () => {
      const snap = storeRef.current;

      // 1. Ensure server-side project exists for each local project
      const projectIdMap = new Map<string, string>(); // localId -> serverId
      for (const project of snap.projects) {
        try {
          const serverProject = await projectApi.create(workspaceId, { title: project.name });
          projectIdMap.set(project.id, serverProject.id);
        } catch {
          // Project may already exist on server — try listing
          try {
            const projects = await projectApi.list(workspaceId);
            const match = projects.find((p: any) => p.title === project.name || p.name === project.name);
            if (match) {
              projectIdMap.set(project.id, match.id);
            }
          } catch {
            // Skip this project on failure
          }
        }
      }

      if (projectIdMap.size === 0) return;

      // 2. Sync diagrams per project
      const diagramIdMap = new Map<string, string>(); // localId -> serverId

      for (const [localProjectId, serverProjectId] of projectIdMap) {
        const localDiagrams = snap.diagrams.filter((d) => d.projectId === localProjectId);
        if (localDiagrams.length === 0) continue;

        const payload = localDiagrams.map((d) => ({
          localId: d.id,
          title: d.title,
          type: d.type,
          mermaidCode: d.mermaidCode,
          excalidrawData: d.excalidrawData,
          theme: d.theme,
          pinned: d.pinned,
          tags: d.tags,
          revisionCursor: d.revisionCursor,
          revisions: d.revisions.map((r) => ({
            mermaidCode: r.mermaidCode,
            source: r.source,
            note: r.note,
            createdAt: r.createdAt,
          })),
          chat: d.chat.map((m) => ({
            role: m.role,
            text: m.text,
            createdAt: m.createdAt,
          })),
        }));

        try {
          const result = await diagramApi.sync(serverProjectId, { diagrams: payload });
          if (result?.mappings) {
            for (const m of result.mappings) {
              diagramIdMap.set(m.localId, m.serverId);
            }
          }
        } catch {
          // Sync failed for this project — diagrams keep local IDs
        }
      }

      // 3. Update local store with server IDs
      if (projectIdMap.size > 0 || diagramIdMap.size > 0) {
        setStore((prev) => {
          const projects = prev.projects.map((p) => {
            const serverId = projectIdMap.get(p.id);
            return serverId ? { ...p, id: serverId } : p;
          });

          const diagrams = prev.diagrams.map((d) => {
            const serverId = diagramIdMap.get(d.id);
            const serverProjectId = projectIdMap.get(d.projectId);
            return {
              ...d,
              ...(serverId ? { id: serverId } : {}),
              ...(serverProjectId ? { projectId: serverProjectId } : {}),
            };
          });

          const selectedProjectId = projectIdMap.get(prev.selectedProjectId) || prev.selectedProjectId;
          const selectedDiagramId = diagramIdMap.get(prev.selectedDiagramId) || prev.selectedDiagramId;

          return { ...prev, projects, diagrams, selectedProjectId, selectedDiagramId };
        });
      }
    };

    run().catch(() => {
      // Silent fail — user can still use the app offline
    });
  }, [workspaceId, setStore]);
}
